import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUser, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { sociosAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import './Socios.css';

const SociosList = () => {
  const [socios, setSocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadSocios();
  }, [statusFilter]);

  useEffect(() => {
    // Reset to first page when search or filter changes
    setCurrentPage(1);
  }, [search, statusFilter]);

  const loadSocios = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const { data } = await sociosAPI.getAll(params);
      setSocios(data.data);
    } catch (error) {
      toast.error('Erro ao carregar sócios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Deseja realmente excluir o sócio ${nome}?`)) {
      return;
    }

    try {
      await sociosAPI.delete(id);
      toast.success('Sócio excluído com sucesso!');
      loadSocios();
    } catch (error) {
      toast.error('Erro ao excluir sócio');
      console.error(error);
    }
  };

  const filteredSocios = socios.filter(socio => {
    const searchLower = search.toLowerCase();
    return (
      socio.nome.toLowerCase().includes(searchLower) ||
      socio.titulo.toLowerCase().includes(searchLower) ||
      (socio.cpf && socio.cpf.includes(search))
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredSocios.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSocios = filteredSocios.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getPaginationRange = () => {
    const range = [];
    const maxButtons = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }
    
    return range;
  };

  if (loading) {
    return (
      <div className="socios-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando sócios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="socios-container">
      <div className="page-header">
        <div>
          <h1><FaUser /> Gestão de Sócios</h1>
          <p>Cadastro e gerenciamento de sócios do clube</p>
        </div>
        {hasPermission('pode_inserir') && (
          <Link to="/socios/novo" className="btn btn-primary">
            <FaPlus /> Novo Sócio
          </Link>
        )}
      </div>

      <div className="filters-card">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nome, título ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Todos</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Por página:</label>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <div className="results-count">
          Mostrando <strong>{startIndex + 1}</strong> a <strong>{Math.min(endIndex, filteredSocios.length)}</strong> de <strong>{filteredSocios.length}</strong> sócio(s)
        </div>
      </div>

      <div className="table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Nome</th>
              <th>CPF</th>
              <th>Telefone</th>
              <th>Email</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {currentSocios.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Nenhum sócio encontrado
                </td>
              </tr>
            ) : (
              currentSocios.map((socio) => (
                <tr key={socio.id}>
                  <td>
                    <span className="badge badge-primary">{socio.titulo}</span>
                  </td>
                  <td>
                    <strong>{socio.nome}</strong>
                  </td>
                  <td>{socio.cpf || '-'}</td>
                  <td>{socio.celular || socio.telefone || '-'}</td>
                  <td>{socio.email || '-'}</td>
                  <td>
                    <span className={`badge ${socio.status === 'ativo' ? 'badge-success' : 'badge-danger'}`}>
                      {socio.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link 
                        to={`/socios/${socio.id}`} 
                        className="btn-icon btn-info"
                        title="Visualizar"
                      >
                        <FaUser />
                      </Link>
                      {hasPermission('pode_alterar') && (
                        <Link 
                          to={`/socios/${socio.id}/editar`} 
                          className="btn-icon btn-warning"
                          title="Editar"
                        >
                          <FaEdit />
                        </Link>
                      )}
                      {hasPermission('pode_deletar') && (
                        <button
                          onClick={() => handleDelete(socio.id, socio.nome)}
                          className="btn-icon btn-danger"
                          title="Excluir"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Página {currentPage} de {totalPages}
            </div>
            
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="Primeira página"
              >
                «
              </button>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >
                <FaChevronLeft />
              </button>

              {getPaginationRange().map((page) => (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Próxima página"
              >
                <FaChevronRight />
              </button>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Última página"
              >
                »
              </button>
            </div>

            <div className="pagination-jump">
              <span>Ir para:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = Number(e.target.value);
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page);
                  }
                }}
                className="page-input"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SociosList;
